import { useEffect, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { extractErrorMessage } from '@/pages/painel-pedidos/lib/errors';
import {
  REGIAO_LABELS,
  TIPO_LABELS,
  SOLUCOES_LABELS,
  formatBRL,
  type RadarOportunidade,
} from '@/pages/painel/lib/radarConstants';
import RadarScore from './RadarScore';

interface Kpis {
  total: number;
  novas: number;
  quentes: number;
  prioridade: number;
  sem_proxima_acao: number;
  acao_semana: number;
  valor_potencial: number;
  lucro_potencial: number;
  convertidas_crm: number;
  descartadas: number;
}

export default function RadarDashboard() {
  const { password } = useOutletContext<{ password: string }>();
  const [kpis, setKpis] = useState<Kpis | null>(null);
  const [porRegiao, setPorRegiao] = useState<{ key: string; count: number }[]>([]);
  const [porTipo, setPorTipo] = useState<{ key: string; count: number }[]>([]);
  const [top, setTop] = useState<RadarOportunidade[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [statsRes, listRes] = await Promise.all([
        supabase.functions.invoke('radar-admin', { body: { action: 'estatisticas', password } }),
        supabase.functions.invoke('radar-admin', { body: { action: 'listar', password, filtros: { status: 'ativas' } } }),
      ]);
      if (statsRes.error) throw new Error(await extractErrorMessage(statsRes.error));
      if (listRes.error) throw new Error(await extractErrorMessage(listRes.error));
      const stats = statsRes.data as { kpis?: Kpis; por_regiao?: any[]; por_tipo?: any[]; error?: string };
      if (stats?.error) throw new Error(stats.error);
      const lista = listRes.data as { oportunidades?: RadarOportunidade[]; error?: string };
      if (lista?.error) throw new Error(lista.error);
      setKpis(stats?.kpis ?? null);
      setPorRegiao(stats?.por_regiao ?? []);
      setPorTipo(stats?.por_tipo ?? []);
      setTop((lista?.oportunidades ?? []).slice(0, 8));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao carregar o radar.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [password]);

  const regiaoMax = Math.max(1, ...porRegiao.map((r) => r.count));
  const tipoMax = Math.max(1, ...porTipo.map((t) => t.count));

  const KPI = ({ label, value, icon, tone = 'text-gray-900' }: { label: string; value: string; icon: string; tone?: string }) => (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <p className="text-[11px] text-gray-500 uppercase tracking-wider font-semibold flex items-center gap-1.5">
        <i className={icon}></i>
        {label}
      </p>
      <p className={`text-2xl font-bold ${tone}`}>{value}</p>
    </div>
  );

  return (
    <div className="space-y-5">
      {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3">{error}</div>}

      {loading && !kpis ? (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-2 border-[#E8610A] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : kpis ? (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            <KPI label="Oportunidades novas" value={String(kpis.novas)} icon="ri-sparkling-2-line" tone="text-[#E8610A]" />
            <KPI label="Oportunidades quentes" value={String(kpis.quentes)} icon="ri-fire-fill" tone="text-red-500" />
            <KPI label="Valor potencial mapeado" value={formatBRL(kpis.valor_potencial)} icon="ri-money-dollar-circle-line" tone="text-gray-900" />
            <KPI label="Lucro potencial estimado" value={formatBRL(kpis.lucro_potencial)} icon="ri-funds-line" tone="text-green-600" />
            <KPI label="Sem próxima ação" value={String(kpis.sem_proxima_acao)} icon="ri-question-line" tone="text-amber-600" />
            <KPI label="Ação esta semana" value={String(kpis.acao_semana)} icon="ri-calendar-check-line" tone="text-gray-900" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-white rounded-2xl border border-gray-200 p-5">
              <h3 className="text-sm font-bold text-gray-900 mb-3">Oportunidades por região</h3>
              {porRegiao.length === 0 ? (
                <p className="text-sm text-gray-400">Sem dados ainda.</p>
              ) : (
                <div className="space-y-2.5">
                  {porRegiao.map((r) => (
                    <div key={r.key} className="flex items-center gap-3">
                      <span className="w-48 text-xs text-gray-600 truncate whitespace-nowrap">
                        {REGIAO_LABELS[r.key] ?? r.key}
                      </span>
                      <div className="flex-1 h-4 bg-gray-100 rounded-md overflow-hidden">
                        <div className="h-full bg-[#E8610A] rounded-md" style={{ width: `${(r.count / regiaoMax) * 100}%` }} />
                      </div>
                      <span className="w-6 text-xs font-semibold text-gray-700 text-right">{r.count}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="bg-white rounded-2xl border border-gray-200 p-5">
              <h3 className="text-sm font-bold text-gray-900 mb-3">Oportunidades por tipo</h3>
              {porTipo.length === 0 ? (
                <p className="text-sm text-gray-400">Sem dados ainda.</p>
              ) : (
                <div className="space-y-2.5">
                  {porTipo.slice(0, 8).map((t) => (
                    <div key={t.key} className="flex items-center gap-3">
                      <span className="w-48 text-xs text-gray-600 truncate whitespace-nowrap">
                        {TIPO_LABELS[t.key] ?? t.key}
                      </span>
                      <div className="flex-1 h-4 bg-gray-100 rounded-md overflow-hidden">
                        <div className="h-full bg-amber-500 rounded-md" style={{ width: `${(t.count / tipoMax) * 100}%` }} />
                      </div>
                      <span className="w-6 text-xs font-semibold text-gray-700 text-right">{t.count}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="font-bold text-gray-900 text-base">Oportunidades priorizadas</h3>
              <Link to="/painel/radar/oportunidades" className="text-xs font-semibold text-[#E8610A] hover:text-[#C4500A] whitespace-nowrap">
                Ver todas <i className="ri-arrow-right-s-line"></i>
              </Link>
            </div>
            {top.length === 0 ? (
              <div className="p-8 text-center text-sm text-gray-400">
                Nenhuma oportunidade ativa ainda. Crie a primeira em <Link to="/painel/radar/oportunidades" className="text-[#E8610A] font-semibold">Oportunidades</Link>.
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {top.map((o) => (
                  <Link
                    key={o.id}
                    to={`/painel/radar/oportunidades/${o.id}`}
                    className="flex items-center gap-4 px-5 py-3.5 hover:bg-gray-50/50 transition-colors cursor-pointer"
                  >
                    <RadarScore op={o} size="sm" />
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-900 truncate">{o.titulo}</p>
                      <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                        {o.empresa && <span className="text-xs text-gray-500 truncate">{o.empresa}</span>}
                        {o.tipo && (
                          <span className="text-[11px] font-semibold text-gray-600 bg-gray-100 rounded-full px-2 py-0.5 whitespace-nowrap">
                            {TIPO_LABELS[o.tipo] ?? o.tipo}
                          </span>
                        )}
                        {(o.solucoes ?? []).slice(0, 3).map((s) => (
                          <span key={s} className="text-[10px] text-[#E8610A] bg-[#E8610A]/10 rounded-full px-2 py-0.5 whitespace-nowrap">
                            {SOLUCOES_LABELS[s] ?? s}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-sm font-bold text-gray-900">{formatBRL(o.valor_potencial_hop ?? o.valor_obra)}</p>
                      <p className="text-xs text-gray-400">{[o.cidade, o.uf].filter(Boolean).join(' - ') || ''}</p>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </>
      ) : null}
    </div>
  );
}